import { DialogClose, DialogTrigger } from '@radix-ui/react-dialog'
import { TrashIcon } from 'lucide-react'
import { useState } from 'react'

import { ClientInvitationDto, useRevokeClientInvitation } from '@/gen/publicApi'

import { Button } from '@/components/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from '@/components/dialog'
import { toast } from '@/components/toast/useToast.ts'

interface Props {
    clientCode: string
    invitation: ClientInvitationDto
    onSuccess: () => void
}

export default function RevokeInvitation({ clientCode, invitation, onSuccess }: Props) {
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)

    const revokeInvitationMutator = useRevokeClientInvitation(clientCode, invitation.id)

    const onRevoke = () => {
        toast({
            title: 'Amending order...',
        })

        // @ts-expect-error - kubb codegen
        revokeInvitationMutator.mutate(undefined, {
            onSuccess: () => {
                onSuccess()
                toast({
                    title: 'Invitation revoked',
                    description: `Invitation revoked successfully`,
                })
            },
            onError: err => {
                console.error('Failed to revoke invitation', err)
                toast({
                    variant: 'destructive',
                    title: 'Revoke failed!',
                    description: `Invitation revoke failed, please try again`,
                })
            },
        })
    }

    if (invitation.tokenState !== 'Pending') {
        return null
    }

    return (
        <>
            <Dialog open={confirmationModalOpen} onOpenChange={setConfirmationModalOpen}>
                <DialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="h-8 gap-1 rounded-3xl hover:text-destructive">
                        <TrashIcon className="h-4 w-4" />
                    </Button>
                </DialogTrigger>
                <DialogContent>
                    <DialogTitle>Are you sure?</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to revoke the invitation for <code>{invitation.email}</code>? This action
                        cannot be undone.
                    </DialogDescription>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button variant="destructive" onClick={onRevoke}>
                            Revoke
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
